import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { Editor } from "./editor/Editor";
import { ResultTable, formatCellValue } from "./results/ResultTable";
import { Loading } from "./common/Loading";
import { QUERY_LOADING_STAGE_LABEL, useQueryLoadingStage } from "../hooks/useQueryLoadingStage";
import { useMobile } from "../hooks/useMobile";
import { Play, CheckCheck, RefreshCw, Lock, Maximize2, ArrowLeft, Copy } from "lucide-react";
import { API_HOST } from "../constants";
import type { QueryStatus } from "../types";

interface CourseCodeBlockProps {
  taskId?: number;
  initialSql?: string;
  user: { id: number } | null;
  isDemo?: boolean; // Если true, то это демонстрация без проверки
  compact?: boolean;
  /** Иллюстративный блок — кнопка Run отключена, SQL только для чтения */
  illustrative?: boolean;
  onTaskCompleted?: (taskId: number) => void; // Callback при успешном выполнении задачи
}

interface ResultGroupItem {
  label: string;
  columns: string[];
  rows: unknown[][];
}

interface QueryResponse {
  rows: unknown[][];
  columns: string[];
  result_groups?: ResultGroupItem[];
  error_msg?: string;
  status?: boolean;
  decision?: string;
  execution_time_ms?: number;
  memory_usage_bytes?: number;
  read_bytes?: number;
  read_rows?: number;
  written_bytes?: number;
  written_rows?: number;
  result_bytes?: number;
  result_rows?: number;
  cpu_time_us?: number;
}

const formatRows = (rows: unknown[][], columns: string[]) => {
  return rows.map((row) => Object.fromEntries(columns.map((c, i) => [c, row[i]])));
};

const formatExecutionTime = (ms: number | null | undefined): string | null => {
  if (ms === null || ms === undefined) {
    return null;
  }
  if (ms < 1) {
    return "< 1ms";
  }
  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  }
  return `${(ms / 1000).toFixed(2)}s`;
};

const formatBytes = (bytes: number | null | undefined): string | null => {
  if (bytes === null || bytes === undefined || bytes === 0) {
    return null;
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(2)}KB`;
  }
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(2)}MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)}GB`;
};

const formatRowsCount = (rows: number | null | undefined): string | null => {
  if (rows === null || rows === undefined || rows === 0) {
    return null;
  }
  if (rows < 1000) {
    return `${rows}`;
  }
  if (rows < 1000000) {
    return `${(rows / 1000).toFixed(1)}K`;
  }
  return `${(rows / 1000000).toFixed(1)}M`;
};

const formatCpuTime = (us: number | null | undefined): string | null => {
  if (us === null || us === undefined || us === 0) {
    return null;
  }
  if (us < 1000) {
    return `${Math.round(us)}µs`;
  }
  if (us < 1000000) {
    return `${(us / 1000).toFixed(2)}ms`;
  }
  return `${(us / 1000000).toFixed(2)}s`;
};

export const CourseCodeBlock = ({
  taskId,
  initialSql = "",
  user,
  isDemo = false,
  compact = false,
  illustrative = false,
  onTaskCompleted,
}: CourseCodeBlockProps) => {
  const isMobile = useMobile();
  const [sql, setSql] = useState(initialSql);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [result, setResult] = useState<Record<string, unknown>[]>([]);
  const [resultColumns, setResultColumns] = useState<string[]>([]);
  const [resultGroups, setResultGroups] = useState<Array<{ label: string; columns: string[]; rows: Record<string, unknown>[] }>>([]);
  const [executionTimeMs, setExecutionTimeMs] = useState<number | null>(null);
  const [memoryUsageBytes, setMemoryUsageBytes] = useState<number | null>(null);
  const [readBytes, setReadBytes] = useState<number | null>(null);
  const [readRows, setReadRows] = useState<number | null>(null);
  const [cpuTimeUs, setCpuTimeUs] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const queryLoadingPhase = useQueryLoadingStage(isLoading);
  const [showExpanded, setShowExpanded] = useState(false);
  const [demoContentHeight, setDemoContentHeight] = useState<number | null>(null);
  const blockRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isDemo) setDemoContentHeight(null);
  }, [isDemo]);

  useEffect(() => {
    setDemoContentHeight(null);
  }, [initialSql]);

  const onDemoContentHeight = useCallback((h: number) => {
    if (typeof window === "undefined") {
      setDemoContentHeight(h);
      return;
    }
    const cap = Math.floor(window.innerHeight * 0.92);
    setDemoContentHeight(Math.min(Math.max(h, 48), cap));
  }, []);

  // В демо: колёсико мыши над блоком SQL прокручивает общий чат, а не редактор
  useEffect(() => {
    if (!isDemo || !blockRef.current) return;
    const el = blockRef.current;
    const handler = (e: WheelEvent) => {
      const scrollParent = (e.target as HTMLElement).closest?.("[data-ai-chat-scroll]") as HTMLElement | null;
      if (scrollParent) {
        e.preventDefault();
        scrollParent.scrollTop += e.deltaY;
      }
    };
    el.addEventListener("wheel", handler, { passive: false, capture: true });
    return () => el.removeEventListener("wheel", handler, { capture: true });
  }, [isDemo]);

  // Вычисляем высоту редактора по числу строк
  const editorHeight = useMemo(() => {
    // Monaco fontSize: 12 (isDemo) / 14 (task). Line height ≈ fontSize * 1.5 rounded.
    const lineHeight = isDemo ? 19 : 21;
    const paddingVertical = 28; // Monaco padding top:12 + bottom:12 + 4px slack
    const minLines = 3;
    const maxLines = isDemo ? 200 : 25;

    const trimmed = sql.replace(/\n+$/, "");
    const lines = trimmed.split("\n").length || 1;
    const actualLines = Math.max(minLines, Math.min(lines, maxLines));

    return actualLines * lineHeight + paddingVertical;
  }, [sql, isDemo]);

  const editorHeightPx = isDemo ? (demoContentHeight ?? editorHeight) : editorHeight;

  const executeQuery = async (endpoint: string, check: boolean) => {
    setIsLoading(true);
    setErrorMsg("");

    try {
      // Для /api/run-query taskId не требуется, для /api/check-query он передаётся в URL
      const body: { query: string } = { query: sql };

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error("Ошибка выполнения запроса");

      const data: QueryResponse = await res.json();

      // Сохраняем все метрики выполнения
      setExecutionTimeMs(data.execution_time_ms ?? null);
      setMemoryUsageBytes(data.memory_usage_bytes ?? null);
      setReadBytes(data.read_bytes ?? null);
      setReadRows(data.read_rows ?? null);
      setCpuTimeUs(data.cpu_time_us ?? null);

      if (check) {
        const formattedRows = formatRows(data.rows || [], data.columns || []);
        setResult(formattedRows);
        setResultColumns(data.columns || []);

        if (data.status === false) {
          setErrorMsg(data.error_msg || data.decision || "Ошибка проверки");
          setStatus("error");
        } else {
          setErrorMsg("");
          setStatus("success");
          // Вызываем callback при успешном выполнении задачи
          if (taskId && onTaskCompleted) {
            onTaskCompleted(taskId);
          }
        }
      } else {
        if (data.error_msg) {
          setErrorMsg(data.error_msg);
          setResult([]);
          setResultColumns([]);
          setResultGroups([]);
          setExecutionTimeMs(null);
          setMemoryUsageBytes(null);
          setReadBytes(null);
          setReadRows(null);
          setCpuTimeUs(null);
          setStatus("error");
          setIsLoading(false);
          return;
        }
        setErrorMsg("");
        if (data.result_groups && data.result_groups.length > 0) {
          const groups = data.result_groups.map((g) => ({
            label: g.label,
            columns: g.columns,
            rows: formatRows(g.rows || [], g.columns),
          }));
          setResultGroups(groups);
          const last = groups[groups.length - 1];
          setResult(last.rows);
          setResultColumns(last.columns);
        } else {
          setResultGroups([]);
          const formattedRows = formatRows(data.rows || [], data.columns || []);
          setResult(formattedRows);
          setResultColumns(data.columns || []);
        }
        setStatus("idle");
      }
    } catch (err) {
      console.error(err);
      setResult([]);
      setResultColumns([]);
      setResultGroups([]);
      setExecutionTimeMs(null);
      setMemoryUsageBytes(null);
      setReadBytes(null);
      setReadRows(null);
      setCpuTimeUs(null);
      setStatus("error");
      setErrorMsg("Ошибка при выполнении запроса");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRun = () => {
    if (!user) {
      setErrorMsg("Авторизуйтесь, чтобы запускать запросы");
      return;
    }
    executeQuery(`${API_HOST}/api/run-query`, false);
  };

  const handleCheck = () => {
    if (isDemo || !taskId) return;
    if (!user) {
      setErrorMsg("Авторизуйтесь, чтобы воспользоваться проверкой");
      return;
    }
    executeQuery(`${API_HOST}/api/check-query/${taskId}`, true);
  };

  const handleReset = () => {
    setSql(initialSql);
    setResult([]);
    setResultColumns([]);
    setResultGroups([]);
    setErrorMsg("");
    setExecutionTimeMs(null);
    setMemoryUsageBytes(null);
    setReadBytes(null);
    setReadRows(null);
    setCpuTimeUs(null);
    setStatus("idle");
  };

  const hasResult = resultGroups.length > 0 || result.length > 0 || resultColumns.length > 0;
  const isEmpty = result.length === 0 && resultColumns.length === 0 && resultGroups.length === 0;
  const isCodeChanged = sql !== initialSql;

  return (
    <>
    <div
      ref={blockRef}
      className={`${compact ? "my-0" : "my-6"} border border-neutral-border rounded-xl bg-white shadow-sm min-w-0 max-w-full overflow-hidden`}
    >
      {/* Панель инструментов — сверху только в режиме задачи (не isDemo) */}
      {!isDemo && (
      <div className="p-2 bg-neutral-light border-b border-neutral-border flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            onClick={handleRun}
            disabled={isLoading || illustrative}
            className="flex items-center justify-center p-1.5 bg-primary text-white rounded hover:bg-primary-dark active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
            title={illustrative ? "Иллюстративный пример — нельзя запустить в учебной среде" : "Запуск"}
          >
            <Play size={14} />
          </button>
          {illustrative && (
            <span className="text-xs text-neutral-text/50 bg-neutral-border/50 rounded px-1.5 py-0.5">
              иллюстративный
            </span>
          )}
          <button
            onClick={handleCheck}
            disabled={isLoading || !user}
            className="flex items-center justify-center p-1.5 text-white rounded active:scale-95 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: user ? "#007AFF" : "#94a3b8" }}
            title={user ? "Проверить" : "Авторизуйтесь для проверки"}
          >
            {!user ? <Lock size={14} /> : <CheckCheck size={14} />}
          </button>
          <button
            onClick={handleReset}
            disabled={isLoading || (!hasResult && !isCodeChanged)}
            className="flex items-center justify-center p-1.5 bg-neutral-light border border-neutral-border rounded hover:bg-neutral-border transition disabled:opacity-50 disabled:cursor-not-allowed"
            title="Сбросить код и очистить результаты"
          >
            <RefreshCw size={14} />
          </button>
        </div>
        {formatExecutionTime(executionTimeMs) && (
          <div className="relative group ml-auto">
            <div className="flex items-center gap-1.5 text-xs text-neutral-text/40 cursor-help">
              <span className="text-neutral-text/50">elapsed:</span>
              <span className="font-mono text-neutral-text/50">{formatExecutionTime(executionTimeMs)}</span>
            </div>
            {(formatExecutionTime(executionTimeMs) || formatRowsCount(readRows) || formatBytes(readBytes)) && (
              <div className="absolute right-0 top-full mt-2 hidden group-hover:block bg-neutral-text text-white text-xs rounded-lg px-3 py-2 shadow-xl z-50 whitespace-nowrap pointer-events-none">
                <div className="flex flex-col gap-1">
                  {formatExecutionTime(executionTimeMs) && (
                    <div className="flex items-center gap-2">
                      <span className="text-neutral-300">elapsed:</span>
                      <span className="font-mono">{formatExecutionTime(executionTimeMs)}</span>
                    </div>
                  )}
                  {formatRowsCount(readRows) && (
                    <div className="flex items-center gap-2">
                      <span className="text-neutral-300">read_rows:</span>
                      <span className="font-mono">{formatRowsCount(readRows)}</span>
                    </div>
                  )}
                  {formatBytes(readBytes) && (
                    <div className="flex items-center gap-2">
                      <span className="text-neutral-300">read_bytes:</span>
                      <span className="font-mono">{formatBytes(readBytes)}</span>
                    </div>
                  )}
                </div>
                <div className="absolute -top-1 right-4 w-2 h-2 bg-neutral-text rotate-45"></div>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Редактор и результаты: горизонтальный скролл внутри блока, страница не сдвигается */}
      <div
        className={`border-b border-neutral-border relative min-w-0 max-w-full overflow-x-auto overflow-y-hidden ${
          isDemo ? "rounded-t-xl" : ""
        }`}
        style={{ zIndex: 1 }}
      >
        <div
          className={`min-w-0 max-w-full ${isDemo ? "rounded-t-xl overflow-hidden bg-white" : ""}`}
          style={{ height: `${editorHeightPx}px`, minWidth: 0 }}
        >
          <Editor
            value={sql}
            onChange={setSql}
            height="100%"
            hideLineNumbers={isDemo}
            disableScroll={isDemo}
            fitContentHeight={isDemo}
            onContentHeightChange={isDemo ? onDemoContentHeight : undefined}
          />
        </div>
      </div>

      {/* Панель инструментов — снизу в demo-режиме (AI Tutor) */}
      {isDemo && (
      <div className="px-2 py-1.5 bg-neutral-light border-t border-neutral-border flex items-center gap-2 flex-wrap rounded-b-xl">
        <div className="flex items-center gap-2">
          <button
            onClick={handleRun}
            disabled={isLoading || illustrative}
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-primary text-white rounded hover:bg-primary-dark active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
            title={illustrative ? "Иллюстративный пример — нельзя запустить в учебной среде" : "Запуск"}
          >
            <Play size={12} />
            {illustrative ? "пример" : "Run"}
          </button>
          <button
            onClick={handleReset}
            disabled={isLoading || (!hasResult && !isCodeChanged)}
            className="flex items-center justify-center p-1.5 bg-white border border-neutral-border rounded hover:bg-neutral-border transition disabled:opacity-50 disabled:cursor-not-allowed"
            title="Сбросить код и очистить результаты"
          >
            <RefreshCw size={12} />
          </button>
          <button
            type="button"
            onClick={() => setShowExpanded(true)}
            className="flex items-center justify-center p-1.5 bg-white border border-neutral-border rounded hover:bg-neutral-border transition"
            title="Открыть в полноэкранном редакторе"
          >
            <Maximize2 size={12} />
          </button>
        </div>
        {formatExecutionTime(executionTimeMs) && (
          <div className="relative group ml-auto">
            <div className="flex items-center gap-1.5 text-xs text-neutral-text/40 cursor-help">
              <span className="text-neutral-text/50">elapsed:</span>
              <span className="font-mono text-neutral-text/50">{formatExecutionTime(executionTimeMs)}</span>
            </div>
            {(formatExecutionTime(executionTimeMs) || formatRowsCount(readRows) || formatBytes(readBytes)) && (
              <div className="absolute right-0 bottom-full mb-2 hidden group-hover:block bg-neutral-text text-white text-xs rounded-lg px-3 py-2 shadow-xl z-50 whitespace-nowrap pointer-events-none">
                <div className="flex flex-col gap-1">
                  {formatExecutionTime(executionTimeMs) && (
                    <div className="flex items-center gap-2">
                      <span className="text-neutral-300">elapsed:</span>
                      <span className="font-mono">{formatExecutionTime(executionTimeMs)}</span>
                    </div>
                  )}
                  {formatRowsCount(readRows) && (
                    <div className="flex items-center gap-2">
                      <span className="text-neutral-300">read_rows:</span>
                      <span className="font-mono">{formatRowsCount(readRows)}</span>
                    </div>
                  )}
                  {formatBytes(readBytes) && (
                    <div className="flex items-center gap-2">
                      <span className="text-neutral-300">read_bytes:</span>
                      <span className="font-mono">{formatBytes(readBytes)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Сообщение об ошибке */}
      {errorMsg && (
        <div className="p-3 bg-red-50 border-b border-red-200 text-red-700 text-sm break-words overflow-wrap-anywhere">
          {errorMsg}
        </div>
      )}

      {/* Сообщение об успехе */}
      {status === "success" && !errorMsg && (
        <div className="p-3 bg-green-50 border-b border-green-200 text-green-700 text-sm">
          ✓ Запрос выполнен успешно!
        </div>
      )}

      {/* Результаты или индикатор загрузки */}
      {isLoading ? (
        <Loading variant="panel" stage={QUERY_LOADING_STAGE_LABEL[queryLoadingPhase]} />
      ) : hasResult ? (
        <div className="p-4 w-full min-w-0 max-w-full space-y-6">
          {resultGroups.length > 1 ? (
            resultGroups.map((group, idx) => (
              <div key={idx} className="space-y-2">
                <div className="flex items-center justify-between gap-2 border-b border-neutral-border pb-1">
                  <span className="text-sm font-medium text-neutral-text">{group.label}</span>
                  <button
                    type="button"
                    onClick={() => {
                      let md = "| " + group.columns.join(" | ") + " |\n";
                      md += "| " + group.columns.map(() => "---").join(" | ") + " |\n";
                      group.rows.slice(0, 10).forEach((r) => {
                        md += "| " + group.columns.map((c) => {
                          const formatted = formatCellValue(r[c]);
                          return formatted.replace(/\|/g, "\\|").replace(/\n/g, "<br>");
                        }).join(" | ") + " |\n";
                      });
                      navigator.clipboard.writeText(md);
                      alert("Результат скопирован в Markdown!");
                    }}
                    className="flex items-center justify-center w-7 h-7 shrink-0 rounded bg-neutral-light border border-neutral-border hover:bg-primary/10 transition"
                    title="Копировать в Markdown"
                    aria-label="Копировать в Markdown"
                  >
                    <Copy size={12} className="text-neutral-text" />
                  </button>
                </div>
                {group.rows.length > 10 && (
                  <div className="mb-2 text-xs text-neutral-text/60 bg-yellow-50 border border-yellow-200 rounded px-2 py-1">
                    Показано 10 из {group.rows.length} строк
                  </div>
                )}
                <div className="w-full min-w-0 overflow-x-auto -mx-1 px-1">
                  <ResultTable
                    rows={group.rows.slice(0, 10)}
                    columns={group.columns}
                    onCopyMarkdown={() => {}}
                    isMobile={isMobile}
                    hideCopyButton
                  />
                </div>
              </div>
            ))
          ) : (
            <>
              {result.length > 10 && (
                <div className="mb-3 text-sm text-neutral-text/60 bg-yellow-50 border border-yellow-200 rounded px-3 py-2">
                  Показано 10 из {result.length} строк. Добавьте LIMIT в запрос для ограничения результатов.
                </div>
              )}
              <div className="w-full min-w-0 overflow-x-auto -mx-1 px-1">
                <ResultTable
                  rows={result.slice(0, 10)}
                  columns={resultColumns}
                  onCopyMarkdown={() => {
                    let md = "| " + resultColumns.join(" | ") + " |\n";
                    md += "| " + resultColumns.map(() => "---").join(" | ") + " |\n";
                    result.slice(0, 10).forEach((r) => {
                      md += "| " + resultColumns.map((c) => {
                        const formatted = formatCellValue(r[c]);
                        return formatted.replace(/\|/g, "\\|").replace(/\n/g, "<br>");
                      }).join(" | ") + " |\n";
                    });
                    navigator.clipboard.writeText(md);
                    alert("Результат скопирован в Markdown!");
                  }}
                  isMobile={isMobile}
                />
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>

    {/* Попап редактора: затемнённый фон и окно по центру */}
    {showExpanded && isDemo && (
      <div
        className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50"
        aria-modal="true"
        role="dialog"
        onClick={() => setShowExpanded(false)}
      >
        <div
          className="flex flex-col bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] min-h-[80vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <header className="flex-shrink-0 flex items-center gap-3 px-4 py-3 border-b border-neutral-border bg-neutral-light/50">
            <button
              type="button"
              onClick={() => setShowExpanded(false)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-neutral-border bg-white hover:bg-neutral-light transition text-neutral-text font-medium"
            >
              <ArrowLeft size={18} />
              Назад
            </button>
            <span className="text-neutral-text font-medium">Редактор SQL — детальное изучение</span>
          </header>
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <div className="flex-shrink-0 p-2 border-b border-neutral-border flex items-center gap-2">
            <button
              onClick={handleRun}
              disabled={isLoading}
              className="flex items-center justify-center p-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition disabled:opacity-50"
              title="Запуск"
            >
              <Play size={16} />
            </button>
            <button
              onClick={handleReset}
              disabled={isLoading || (!hasResult && !isCodeChanged)}
              className="flex items-center justify-center p-2 bg-white border border-neutral-border rounded-lg hover:bg-neutral-light transition disabled:opacity-50"
              title="Сбросить"
            >
              <RefreshCw size={16} />
            </button>
            {formatExecutionTime(executionTimeMs) && (
              <span className="text-sm text-neutral-text/60 ml-2">
                {formatExecutionTime(executionTimeMs)}
              </span>
            )}
          </div>
          <div className="flex-shrink-0 flex flex-col overflow-hidden border-b border-neutral-border" style={{ height: "280px" }}>
            <div className="flex-1 min-h-0 w-full" style={{ height: "280px" }}>
              <Editor value={sql} onChange={setSql} height="280px" />
            </div>
          </div>
          {errorMsg && (
            <div className="flex-shrink-0 p-3 bg-red-50 border-b border-red-200 text-red-700 text-sm">
              {errorMsg}
            </div>
          )}
          {status === "success" && !errorMsg && (
            <div className="flex-shrink-0 p-3 bg-green-50 border-b border-green-200 text-green-700 text-sm">
              ✓ Запрос выполнен успешно
            </div>
          )}
          {hasResult && (
            <div className="flex-1 min-h-0 overflow-auto overflow-x-auto p-4 space-y-6">
              {resultGroups.length > 1 ? (
                resultGroups.map((group, idx) => (
                  <div key={idx} className="space-y-2 min-w-0">
                    <div className="flex items-center justify-between gap-2 border-b border-neutral-border pb-1">
                      <span className="text-sm font-medium text-neutral-text">{group.label}</span>
                      <button
                        type="button"
                        onClick={() => {
                          let md = "| " + group.columns.join(" | ") + " |\n";
                          md += "| " + group.columns.map(() => "---").join(" | ") + " |\n";
                          group.rows.slice(0, 10).forEach((r) => {
                            md += "| " + group.columns.map((c) => formatCellValue(r[c]).replace(/\|/g, "\\|").replace(/\n/g, "<br>")).join(" | ") + " |\n";
                          });
                          navigator.clipboard.writeText(md);
                        }}
                        className="flex items-center justify-center w-7 h-7 shrink-0 rounded bg-neutral-light border border-neutral-border hover:bg-primary/10 transition"
                        title="Копировать в Markdown"
                        aria-label="Копировать в Markdown"
                      >
                        <Copy size={12} className="text-neutral-text" />
                      </button>
                    </div>
                    {group.rows.length > 10 && (
                      <div className="text-xs text-neutral-text/60 bg-yellow-50 border border-yellow-200 rounded px-2 py-1">
                        Показано 10 из {group.rows.length} строк
                      </div>
                    )}
                    <div className="overflow-x-auto min-w-0 max-w-full">
                    <ResultTable
                      rows={group.rows.slice(0, 10)}
                      columns={group.columns}
                      onCopyMarkdown={() => {}}
                      isMobile={isMobile}
                      hideCopyButton
                    />
                    </div>
                  </div>
                ))
              ) : (
                <>
                  {result.length > 10 && (
                    <div className="mb-3 text-sm text-neutral-text/60 bg-yellow-50 border border-yellow-200 rounded px-3 py-2">
                      Показано 10 из {result.length} строк
                    </div>
                  )}
                  <div className="overflow-x-auto min-w-0 max-w-full">
                  <ResultTable
                    rows={result.slice(0, 10)}
                    columns={resultColumns}
                    onCopyMarkdown={() => {
                      let md = "| " + resultColumns.join(" | ") + " |\n";
                      md += "| " + resultColumns.map(() => "---").join(" | ") + " |\n";
                      result.slice(0, 10).forEach((r) => {
                        md += "| " + resultColumns.map((c) => formatCellValue(r[c]).replace(/\|/g, "\\|").replace(/\n/g, "<br>")).join(" | ") + " |\n";
                      });
                      navigator.clipboard.writeText(md);
                    }}
                    isMobile={isMobile}
                  />
                  </div>
                </>
              )}
            </div>
          )}
          {isLoading && (
            <div className="flex-shrink-0 border-b border-neutral-border">
              <Loading variant="panel" stage={QUERY_LOADING_STAGE_LABEL[queryLoadingPhase]} />
            </div>
          )}
          </div>
        </div>
      </div>
    )}

    </>
  );
};

